<script setup lang="ts">
import LeadFormBlock from '@/client/components/blocks/LeadFormBlock.vue';
import SiteLayout from '@/client/layouts/SiteLayout.vue';
import type { LeadFormContent, SitePageProps } from '@/client/types';
import { Button } from '@/components/ui/button';
import { Head, Link } from '@inertiajs/vue3';
import { Boxes } from 'lucide-vue-next';

/**
 * The page of a product: its picture and description, and the request form with the product already chosen.
 */
const props = defineProps<
    SitePageProps & {
        product: App.Data.SiteProductData;
        products: App.Data.SiteProductData[];
        lead: LeadFormContent;
        sources: { id: number; name: string }[];
        captcha: App.Data.CaptchaWidgetData | null;
    }
>();

const FORM_ID = 'lead';
</script>

<template>
    <Head :title="`${product.title} — ${brandName}`" />

    <SiteLayout :site="props" :cta-href="`#${FORM_ID}`">
        <article class="mx-auto grid max-w-6xl gap-10 px-4 pt-12 md:grid-cols-2 md:items-start md:pt-16">
            <div class="grid gap-6">
                <Link :href="homeUrl" class="text-sm text-muted-foreground hover:text-foreground">← {{ $t('site.back_home') }}</Link>
                <h1 class="text-3xl font-semibold tracking-tight md:text-5xl">{{ product.title }}</h1>
                <p v-if="product.summary" class="text-lg leading-relaxed text-muted-foreground">{{ product.summary }}</p>
                <div>
                    <Button as-child size="lg">
                        <a :href="`#${FORM_ID}`">{{ $t('site.product_request') }}</a>
                    </Button>
                </div>
            </div>
            <img v-if="product.image_url" :src="product.image_url" :alt="product.title" class="aspect-[4/3] w-full rounded-2xl border object-cover" />
            <div v-else class="grid aspect-[4/3] w-full place-items-center rounded-2xl border bg-muted text-muted-foreground">
                <Boxes class="size-14" />
            </div>
            <p v-if="product.description" class="leading-relaxed whitespace-pre-line text-muted-foreground md:col-span-2">{{ product.description }}</p>
        </article>

        <LeadFormBlock
            :id="FORM_ID"
            :content="lead"
            :captcha="captcha"
            :privacy-url="privacyUrl"
            :offer-url="offerUrl"
            :products="products"
            :product="product.code"
            :sources="sources"
        />
    </SiteLayout>
</template>
